import { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Card, Form, Modal, Nav, Spinner, Tab } from 'react-bootstrap';
import { useHistory, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  listCarotidaDictionaryItems,
  resolveCarotidaDictionaryItem,
  selectCarotidaById,
  updateCarotida,
  upsertCarotidaDictionaryItem,
} from 'services/carotida';
import 'styles/laudo-base.css';
import ListaDicionarioComposta from 'components/laudos/ListaDicionarioComposta';
import useSWR, { mutate } from 'swr';

const bottomTabList = [
  { key: 'dados-pessoais', label: 'Dados Pessoais' },
  { key: 'sistema-direito', label: 'Sistema Carotideo Direito' },
  { key: 'sistema-esquerdo', label: 'Sistema Carotideo Esquerdo' },
  { key: 'arterias-vertebrais', label: 'Arterias Vertebrais' },
  { key: 'observacoes', label: 'Observacoes' },
];

const tabHeadingMap = {
  'dados-pessoais': 'Dados Pessoais',
  'sistema-direito': 'Sistema Carotideo Direito',
  'sistema-esquerdo': 'Sistema Carotideo Esquerdo',
  'arterias-vertebrais': 'Arterias Vertebrais',
  observacoes: 'Observacoes',
};

const listConfig = {
  ccd: { title: 'Carotida Comum Direita', group: 'ccd' },
  cid: { title: 'Carotida Interna Direita', group: 'cid' },
  ced: { title: 'Carotida Externa Direita', group: 'ced' },
  cce: { title: 'Carotida Comum Esquerda', group: 'cce' },
  cie: { title: 'Carotida Interna Esquerda', group: 'cie' },
  cee: { title: 'Carotida Externa Esquerda', group: 'cee' },
  avd: { title: 'Arteria Vertebral Direita', group: 'avd' },
  ave: { title: 'Arteria Vertebral Esquerda', group: 'ave' },
};

const sectionMap = {
  'sistema-direito': ['ccd', 'cid', 'ced'],
  'sistema-esquerdo': ['cce', 'cie', 'cee'],
  'arterias-vertebrais': ['avd', 'ave'],
};

const listKeys = Object.keys(listConfig);

const dictionaryTabList = [
  { key: 'anatomia', label: 'Anatomia' },
  { key: 'velocidade', label: 'Velocidade' },
  { key: 'intensidade', label: 'Intensidade' },
];

const dictionaryEnabled = true;

function normalizeCode(value) {
  return String(value ?? '').trim();
}

function buildGroupCandidates(listKey, group, type) {
  const suffix = type.charAt(0).toUpperCase() + type.slice(1);
  return Array.from(
    new Set([
      `${group}_${type}`,
      `${group}-${type}`,
      `${group}${suffix}`,
      `${type}_${group}`,
      `${listKey}_${type}`,
      `${listKey}-${type}`,
      `${listKey}${suffix}`,
      type,
    ])
  );
}

function getColumnsForList(listKey) {
  const cfg = listConfig[listKey];
  const group = cfg?.group || listKey;
  return [
    {
      codeKey: 'CODANATOMIA',
      codeLabel: 'Anat',
      textKey: 'ANATOMIA',
      textLabel: 'Anatomia',
      groups: buildGroupCandidates(listKey, group, 'anatomia'),
      textMinWidth: 260,
    },
    {
      codeKey: 'CODVELOCIDADE',
      codeLabel: 'Vel',
      textKey: 'VELOCIDADE',
      textLabel: 'Velocidade',
      groups: buildGroupCandidates(listKey, group, 'velocidade'),
      textMinWidth: 190,
    },
    {
      codeKey: 'CODINTENSIDADE',
      codeLabel: 'Int',
      textKey: 'INTENSIDADE',
      textLabel: 'Intensidade',
      groups: buildGroupCandidates(listKey, group, 'intensidade'),
      textMinWidth: 190,
    },
  ];
}

export default function FormCarotida() {
  const history = useHistory();
  const { id } = useParams();
  const {
    data: carotida,
    isLoading,
    error,
  } = useSWR(id ? ['carotida', id] : null, () => selectCarotidaById(id));
  const [formData, setFormData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('dados-pessoais');
  const [resolvingRows, setResolvingRows] = useState({});
  const dictionaryCacheRef = useRef(new Map());
  const [showDictionaryModal, setShowDictionaryModal] = useState(false);
  const [activeDictionaryTab, setActiveDictionaryTab] = useState(dictionaryTabList[0].key);
  const [dictionaryData, setDictionaryData] = useState({});
  const [dictionaryLoading, setDictionaryLoading] = useState({});
  const [dictionaryDraft, setDictionaryDraft] = useState({});
  const [savingDictionaryKey, setSavingDictionaryKey] = useState('');
  const scan = carotida?.carotida?.[0];

  useEffect(() => {
    if (!carotida || !scan) return;

    const next = {
      OBSERVACOES: scan.OBSERVACOES ?? '',
    };

    listKeys.forEach((key) => {
      next[key] = Array.isArray(carotida[key])
        ? carotida[key].map((item) => ({
            CODANATOMIA: normalizeCode(item?.CODANATOMIA),
            ANATOMIA: String(item?.ANATOMIA ?? ''),
            CODVELOCIDADE: normalizeCode(item?.CODVELOCIDADE),
            VELOCIDADE: String(item?.VELOCIDADE ?? ''),
            CODINTENSIDADE: normalizeCode(item?.CODINTENSIDADE),
            INTENSIDADE: String(item?.INTENSIDADE ?? ''),
            MEDIDA: item?.MEDIDA,
          }))
        : [];
    });

    setFormData(next);
  }, [carotida, scan]);

  const resolveDictionaryValue = useCallback(async ({ groups, code }) => {
    if (!dictionaryEnabled) return null;

    const normalizedCode = normalizeCode(code);
    if (!normalizedCode || !Array.isArray(groups) || groups.length === 0) return null;

    for (const group of groups) {
      const normalizedGroup = normalizeCode(group);
      if (!normalizedGroup) continue;

      const cacheKey = `${normalizedGroup}|${normalizedCode}`;
      if (dictionaryCacheRef.current.has(cacheKey)) {
        const cached = dictionaryCacheRef.current.get(cacheKey);
        if (cached) return cached;
      }

      const resolved = await resolveCarotidaDictionaryItem({
        grupo: normalizedGroup,
        codigo: normalizedCode,
      });

      dictionaryCacheRef.current.set(cacheKey, resolved || null);
      if (resolved) return resolved;
    }

    return null;
  }, []);

  useEffect(() => {
    if (!dictionaryEnabled || !formData) return;

    let cancelled = false;

    const hydrateDescriptions = async () => {
      const updates = [];

      for (const listKey of listKeys) {
        const items = Array.isArray(formData[listKey]) ? formData[listKey] : [];
        const columns = getColumnsForList(listKey);

        for (let index = 0; index < items.length; index += 1) {
          const item = items[index] || {};
          const patch = {};
          let hasPatch = false;

          for (const column of columns) {
            const codeValue = normalizeCode(item?.[column.codeKey]);
            const currentText = String(item?.[column.textKey] ?? '').trim();
            if (!codeValue || currentText) continue;

            const resolved = await resolveDictionaryValue({
              groups: column.groups,
              code: codeValue,
            });

            if (resolved?.descricao) {
              patch[column.textKey] = resolved.descricao;
              patch[column.codeKey] = resolved.codigo || codeValue;
              hasPatch = true;
            }
          }

          if (hasPatch) updates.push({ listKey, index, patch });
        }
      }

      if (cancelled || updates.length === 0) return;

      setFormData((prev) => {
        if (!prev) return prev;
        const next = { ...prev };
        updates.forEach(({ listKey, index, patch }) => {
          const list = Array.isArray(next[listKey]) ? [...next[listKey]] : [];
          if (!list[index]) return;
          list[index] = { ...list[index], ...patch };
          next[listKey] = list;
        });
        return next;
      });
    };

    hydrateDescriptions();

    return () => {
      cancelled = true;
    };
  }, [formData, resolveDictionaryValue]);

  const setField = (field, value) => {
    setFormData((prev) => ({ ...(prev || {}), [field]: value }));
  };

  const setListField = (listKey, index, field, value) => {
    setFormData((prev) => {
      if (!prev) return prev;
      const list = Array.isArray(prev[listKey]) ? [...prev[listKey]] : [];
      if (!list[index]) return prev;
      list[index] = { ...list[index], [field]: value };
      return { ...prev, [listKey]: list };
    });
  };

  const setListFields = (listKey, index, patch) => {
    setFormData((prev) => {
      if (!prev) return prev;
      const list = Array.isArray(prev[listKey]) ? [...prev[listKey]] : [];
      if (!list[index]) return prev;
      list[index] = { ...list[index], ...patch };
      return { ...prev, [listKey]: list };
    });
  };

  const addListItem = (listKey) => {
    const columns = getColumnsForList(listKey);
    setFormData((prev) => {
      if (!prev) return prev;
      const list = Array.isArray(prev[listKey]) ? [...prev[listKey]] : [];
      const row = {};
      columns.forEach((column) => {
        row[column.codeKey] = '';
        row[column.textKey] = '';
      });
      row.MEDIDA = null;
      list.push(row);
      return { ...prev, [listKey]: list };
    });
  };

  const removeListItem = (listKey, index) => {
    setFormData((prev) => {
      if (!prev) return prev;
      const list = Array.isArray(prev[listKey]) ? [...prev[listKey]] : [];
      if (!list[index]) return prev;
      list.splice(index, 1);
      return { ...prev, [listKey]: list };
    });
  };

  const handleResolveListCode = async (listKey, index, targetCodeKey, codeValue) => {
    if (!dictionaryEnabled) {
      setListField(listKey, index, targetCodeKey, normalizeCode(codeValue));
      return;
    }

    const columns = getColumnsForList(listKey);
    const normalizedCode = normalizeCode(codeValue);
    const currentItem = formData?.[listKey]?.[index] || {};
    const draftItem = { ...currentItem, [targetCodeKey]: normalizedCode };
    const resolvingKey = `${listKey}:${index}`;

    if (!columns.some((column) => normalizeCode(draftItem[column.codeKey]))) {
      const emptyPatch = columns.reduce(
        (patch, column) => ({
          ...patch,
          [column.codeKey]: '',
          [column.textKey]: '',
        }),
        {}
      );
      setListFields(listKey, index, emptyPatch);
      return;
    }

    setResolvingRows((prev) => ({ ...prev, [resolvingKey]: true }));

    try {
      const nextPatch = {};

      for (const column of columns) {
        const columnCode = normalizeCode(draftItem[column.codeKey]);
        if (!columnCode) {
          nextPatch[column.codeKey] = '';
          nextPatch[column.textKey] = '';
          continue;
        }

        const resolved = await resolveDictionaryValue({
          groups: column.groups,
          code: columnCode,
        });

        nextPatch[column.codeKey] = resolved?.codigo || columnCode;
        nextPatch[column.textKey] = resolved?.descricao || '';
      }

      setListFields(listKey, index, nextPatch);
    } catch (err) {
      setListField(listKey, index, targetCodeKey, normalizedCode);
      toast.error(err?.response?.data?.message || 'Erro ao consultar dicionario da carotida.');
    } finally {
      setResolvingRows((prev) => {
        const next = { ...prev };
        delete next[resolvingKey];
        return next;
      });
    }
  };

  const getCompositeHandlers = (listKey) => ({
    editable: true,
    columns: getColumnsForList(listKey),
    onChangeCode: (index, value, targetCodeKey) =>
      setListField(listKey, index, targetCodeKey, value),
    onResolveCode: (index, value, targetCodeKey) =>
      handleResolveListCode(listKey, index, targetCodeKey, value),
    onAddItem: () => addListItem(listKey),
    onRemoveItem: (index) => removeListItem(listKey, index),
    resolvingKeySet: new Set(
      Object.keys(resolvingRows)
        .filter((key) => key.startsWith(`${listKey}:`))
        .map((key) => key.split(':')[1])
    ),
  });

  const loadDictionaryTab = async (groupKey, { force = false } = {}) => {
    if (!dictionaryEnabled) return;
    if (!groupKey) return;
    if (!force && Array.isArray(dictionaryData[groupKey])) return;

    setDictionaryLoading((prev) => ({ ...prev, [groupKey]: true }));
    try {
      const list = await listCarotidaDictionaryItems({ grupo: groupKey });
      setDictionaryData((prev) => ({ ...prev, [groupKey]: list }));
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Erro ao carregar dicionario.');
    } finally {
      setDictionaryLoading((prev) => ({ ...prev, [groupKey]: false }));
    }
  };

  const handleOpenDictionaryModal = () => {
    if (!dictionaryEnabled) return;
    setShowDictionaryModal(true);
    loadDictionaryTab(activeDictionaryTab);
  };

  const handleChangeDictionaryCell = (groupKey, index, field, value) => {
    setDictionaryData((prev) => {
      const list = Array.isArray(prev[groupKey]) ? [...prev[groupKey]] : [];
      if (!list[index]) return prev;
      list[index] = { ...list[index], [field]: value };
      return { ...prev, [groupKey]: list };
    });
  };

  const handleSaveDictionaryRow = async (groupKey, row) => {
    const codigo = normalizeCode(row?.codigo);
    const descricao = String(row?.descricao || '').trim();
    if (!codigo || !descricao) {
      toast.warn('Codigo e descricao sao obrigatorios.');
      return;
    }

    const saveKey = `${groupKey}:${codigo}`;
    try {
      setSavingDictionaryKey(saveKey);
      await upsertCarotidaDictionaryItem({
        grupo: groupKey,
        codigo,
        descricao,
      });
      toast.success('Dicionario atualizado.');
      await loadDictionaryTab(groupKey, { force: true });
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Erro ao salvar item do dicionario.');
    } finally {
      setSavingDictionaryKey('');
    }
  };

  const handleSaveDictionaryDraft = async (groupKey) => {
    const draft = dictionaryDraft[groupKey] || { codigo: '', descricao: '' };
    const codigo = normalizeCode(draft.codigo);
    const descricao = String(draft.descricao || '').trim();

    if (!codigo || !descricao) {
      toast.warn('Informe codigo e descricao para incluir novo item.');
      return;
    }

    const saveKey = `${groupKey}:${codigo}`;
    try {
      setSavingDictionaryKey(saveKey);
      await upsertCarotidaDictionaryItem({
        grupo: groupKey,
        codigo,
        descricao,
      });
      setDictionaryDraft((prev) => ({
        ...prev,
        [groupKey]: { codigo: '', descricao: '' },
      }));
      toast.success('Novo item incluido no dicionario.');
      await loadDictionaryTab(groupKey, { force: true });
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Erro ao incluir item no dicionario.');
    } finally {
      setSavingDictionaryKey('');
    }
  };

  const renderSectionLists = (tabKey) => {
    const sectionLists = sectionMap[tabKey] || [];
    if (!formData) return null;

    return sectionLists.map((listKey) => (
      <ListaDicionarioComposta
        key={listKey}
        title={`${listConfig[listKey].title}:`}
        items={formData[listKey]}
        {...getCompositeHandlers(listKey)}
      />
    ));
  };

  const renderTabContent = () => {
    if (!formData) return null;

    return {
      'dados-pessoais': (
        <div className="row g-3 legacy-panel-grid">
          <div className="col-lg-6">
            <div className="legacy-field-block h-100">
              <div className="legacy-field-label">Paciente</div>
              <div className="legacy-field-value">
                {scan?.CODPAC} | {scan?.NOMEPAC}
              </div>
            </div>
          </div>
          <div className="col-lg-3">
            <div className="legacy-field-block h-100">
              <div className="legacy-field-label">Data Nasc</div>
              <div className="legacy-field-value">{scan?.dataNascFmt || '-'}</div>
            </div>
          </div>
          <div className="col-lg-3">
            <div className="legacy-field-block h-100">
              <div className="legacy-field-label">Sexo</div>
              <div className="legacy-field-value">{scan?.SEXOPAC || '-'}</div>
            </div>
          </div>
          <div className="col-lg-4">
            <div className="legacy-field-block h-100">
              <div className="legacy-field-label">Idade</div>
              <div className="legacy-field-value">
                {scan?.idadePAC != null ? `${scan.idadePAC} anos` : '-'}
              </div>
            </div>
          </div>
          <div className="col-lg-4">
            <div className="legacy-field-block h-100">
              <div className="legacy-field-label">Convenio</div>
              <div className="legacy-field-value">{scan?.SIGLACONV || '-'}</div>
            </div>
          </div>
          <div className="col-lg-4">
            <div className="legacy-field-block h-100">
              <div className="legacy-field-label">Codigo</div>
              <div className="legacy-field-value">{scan?.CODSCAN || '-'}</div>
            </div>
          </div>
          <div className="col-lg-4">
            <div className="legacy-field-block h-100">
              <div className="legacy-field-label">Data do Exame</div>
              <div className="legacy-field-value">{scan?.dataScanFmt || '-'}</div>
            </div>
          </div>
          <div className="col-lg-4">
            <div className="legacy-field-block h-100">
              <div className="legacy-field-label">Medico Realizou</div>
              <div className="legacy-field-value">{scan?.Realizou || '-'}</div>
            </div>
          </div>
          <div className="col-lg-4">
            <div className="legacy-field-block h-100">
              <div className="legacy-field-label">Medico Solicitou</div>
              <div className="legacy-field-value">{scan?.Solicitante || '-'}</div>
            </div>
          </div>
        </div>
      ),
      'sistema-direito': renderSectionLists('sistema-direito'),
      'sistema-esquerdo': renderSectionLists('sistema-esquerdo'),
      'arterias-vertebrais': renderSectionLists('arterias-vertebrais'),
      observacoes: (
        <Form.Group>
          <strong>Obs:</strong>
          <Form.Control
            as="textarea"
            rows={Math.max(6, (String(formData.OBSERVACOES || '').match(/\n/g) || []).length + 3)}
            className="legacy-observacoes-area"
            value={formData.OBSERVACOES ?? ''}
            onChange={(e) => setField('OBSERVACOES', e.target.value)}
          />
        </Form.Group>
      ),
    };
  };

  const handleGravar = async () => {
    if (!id) return toast.warn('Erro de validação!');
    try {
      setSaving(true);
      const payload = {
        OBSERVACOES: formData?.OBSERVACOES ?? '',
      };
      listKeys.forEach((key) => {
        payload[key] = Array.isArray(formData?.[key]) ? formData[key] : [];
      });

      const res = await updateCarotida(id, payload);
      await mutate(['carotida', id]);
      toast.success(res.message || 'Laudo atualizado.');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erro ao gravar.');
    } finally {
      setSaving(false);
    }
  };

  const handleAnexar = () => toast.info('em desenvolvimento...');
  const handleVisualizar = () => {
    if (!id) {
      toast.warn('Erro de validação!');
      return;
    }
    history.push(`/visualizacao-carotida/${id}`);
  };

  return (
    <Card className="mb-3 mt-2 no-print legacy-eco-card">
      <Card.Header className="legacy-header">
        <div className="legacy-header-side legacy-header-side-left">
          <div className="legacy-header-eco-wrap">
            <div className="legacy-mini-label">Scan N</div>
            <div className="legacy-mini-value">{scan?.CODSCAN || '-'}</div>
          </div>
        </div>
        <div className="legacy-header-title-wrap text-center">
          <div className="legacy-header-title">Eco Scan</div>
        </div>
        <div className="legacy-header-side legacy-header-side-right">
          <div className="legacy-header-date-wrap">
            <div className="legacy-mini-label">Data</div>
            <div className="legacy-mini-value">{scan?.dataScanFmt || '-'}</div>
          </div>
          {dictionaryEnabled ? (
            <Button
              variant="outline-primary"
              className="legacy-dictionaries-btn"
              onClick={handleOpenDictionaryModal}
            >
              Dicionarios
            </Button>
          ) : null}
        </div>
      </Card.Header>
      {isLoading && (
        <Card.Body>
          <div className="d-flex justify-content-center align-items-center py-5">
            <Spinner animation="border" variant="primary" />
          </div>
        </Card.Body>
      )}
      {error && !isLoading && (
        <Card.Body className="text-center text-danger py-5">
          {error.message || 'Erro ao carregar o laudo.'}
        </Card.Body>
      )}
      {carotida && scan && !isLoading && !error && formData && (
        <>
          <Card.Body className="legacy-card-body">
            <div className="legacy-content-shell container-fluid px-0">
              <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k || activeTab)}>
                <Nav variant="pills" className="legacy-tabs-row legacy-tabs-row-bottom flex-wrap">
                  {bottomTabList.map((tab) => (
                    <Nav.Item key={tab.key}>
                      <Nav.Link eventKey={tab.key} className="legacy-tab-link">
                        {tab.label}
                      </Nav.Link>
                    </Nav.Item>
                  ))}
                </Nav>

                <div className="legacy-section-title-wrap">
                  <div className="legacy-section-title">
                    {tabHeadingMap[activeTab] || 'Eco Scan'}
                  </div>
                </div>

                <Tab.Content className="legacy-tab-content">
                  {bottomTabList.map((tab) => (
                    <Tab.Pane key={tab.key} eventKey={tab.key}>
                      {renderTabContent()?.[tab.key] || null}
                    </Tab.Pane>
                  ))}
                </Tab.Content>
              </Tab.Container>
            </div>
          </Card.Body>
          <Card.Footer className="legacy-footer no-print d-flex flex-wrap justify-content-center gap-2">
            <Button variant="success" onClick={handleGravar} disabled={saving}>
              {saving ? <Spinner animation="border" size="sm" /> : 'Gravar'}
            </Button>{' '}
            <Button variant="secondary" onClick={handleVisualizar}>
              Visualizar
            </Button>{' '}
            <Button variant="info" onClick={handleAnexar}>
              Anexar
            </Button>
          </Card.Footer>

          {dictionaryEnabled ? (
            <Modal
              size="xl"
              show={showDictionaryModal}
              onHide={() => setShowDictionaryModal(false)}
              centered
            >
              <Modal.Header closeButton>
                <Modal.Title>Dicionarios da Carotida</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <Tab.Container
                  activeKey={activeDictionaryTab}
                  onSelect={(k) => {
                    const next = k || activeDictionaryTab;
                    setActiveDictionaryTab(next);
                    loadDictionaryTab(next);
                  }}
                >
                  <Nav variant="tabs" className="legacy-dictionary-tabs">
                    {dictionaryTabList.map((tab) => (
                      <Nav.Item key={tab.key}>
                        <Nav.Link eventKey={tab.key}>{tab.label}</Nav.Link>
                      </Nav.Item>
                    ))}
                  </Nav>

                  <Tab.Content className="legacy-dictionary-content">
                    {dictionaryTabList.map((tab) => {
                      const list = Array.isArray(dictionaryData[tab.key])
                        ? dictionaryData[tab.key]
                        : [];
                      const draft = dictionaryDraft[tab.key] || {
                        codigo: '',
                        descricao: '',
                      };
                      const loading = dictionaryLoading[tab.key];

                      return (
                        <Tab.Pane key={tab.key} eventKey={tab.key}>
                          <div className="legacy-dictionary-new-row">
                            <Form.Control
                              type="text"
                              placeholder="Codigo"
                              value={draft.codigo}
                              onChange={(e) =>
                                setDictionaryDraft((prev) => ({
                                  ...prev,
                                  [tab.key]: {
                                    ...draft,
                                    codigo: e.target.value,
                                  },
                                }))
                              }
                            />
                            <Form.Control
                              type="text"
                              placeholder="Descricao"
                              value={draft.descricao}
                              onChange={(e) =>
                                setDictionaryDraft((prev) => ({
                                  ...prev,
                                  [tab.key]: {
                                    ...draft,
                                    descricao: e.target.value,
                                  },
                                }))
                              }
                            />
                            <Button
                              variant="success"
                              onClick={() => handleSaveDictionaryDraft(tab.key)}
                              disabled={savingDictionaryKey.startsWith(`${tab.key}:`)}
                            >
                              Incluir
                            </Button>
                          </div>

                          {loading ? (
                            <div className="text-center py-4">
                              <Spinner animation="border" size="sm" />
                            </div>
                          ) : (
                            <div className="legacy-dictionary-grid">
                              {list.map((row, index) => {
                                const rowKey = `${tab.key}:${row.codigo}`;
                                return (
                                  <div
                                    className="legacy-dictionary-row"
                                    key={rowKey || `${tab.key}-${index}`}
                                  >
                                    <Form.Control
                                      type="text"
                                      value={row.codigo}
                                      onChange={(e) =>
                                        handleChangeDictionaryCell(
                                          tab.key,
                                          index,
                                          'codigo',
                                          e.target.value
                                        )
                                      }
                                    />
                                    <Form.Control
                                      type="text"
                                      value={row.descricao}
                                      onChange={(e) =>
                                        handleChangeDictionaryCell(
                                          tab.key,
                                          index,
                                          'descricao',
                                          e.target.value
                                        )
                                      }
                                    />
                                    <Button
                                      variant="primary"
                                      onClick={() => handleSaveDictionaryRow(tab.key, row)}
                                      disabled={savingDictionaryKey === rowKey}
                                    >
                                      Salvar
                                    </Button>
                                  </div>
                                );
                              })}

                              {list.length === 0 ? (
                                <p className="text-muted mb-0">
                                  Nenhum item encontrado neste dicionario.
                                </p>
                              ) : null}
                            </div>
                          )}
                        </Tab.Pane>
                      );
                    })}
                  </Tab.Content>
                </Tab.Container>
              </Modal.Body>
            </Modal>
          ) : null}
        </>
      )}
    </Card>
  );
}
